/**
 * Até que tier cada perícia vai em cada área da Season 1.
 *
 * <p>A regra é do Theo, e tem três degraus:
 *
 * <ol>
 *   <li><b>T1 e T2 existem em toda área, de toda perícia.</b> Por isso não se marcam: um
 *       campo que só admite uma resposta não é escolha, e guardá-lo seria guardar o
 *       resultado da regra em vez da regra.</li>
 *   <li><b>T3 existe em todas as regiões</b>, mas só em algumas áreas de cada — é o time
 *       que escolhe em quais.</li>
 *   <li><b>T4 e T5 são exclusivos</b>: cada região é a única dona de uma coleta e da
 *       criação que sai dela. Fora da região dona, o teto da perícia é T3.</li>
 * </ol>
 *
 * <p>O valor guardado é o <b>teto</b> — o tier mais alto daquela perícia na área. Quem
 * marca T5 diz que ali se coleta do T1 ao T5, e não só o T5: é a mesma leitura do tier da
 * área, que diz até onde a dificuldade chega.
 */
import { COLETAS, PERICIAS } from './pericias';
import type { Pericia } from './pericias';

/** O tier que toda área tem, de toda perícia, sem ninguém marcar. */
export const TIER_DE_TODA_AREA = 2;

/** Os tiers que se escolhem por área, do menor ao maior. */
export const TIERS_ESCOLHIDOS = [3, 4, 5] as const;

/**
 * A região dona do T4 e do T5 de cada perícia, por <b>slot</b> do mapa.
 *
 * <p>Slot e não nome pela mesma razão da vocação: renomear a região não a muda de lugar.
 * Tecnomagia não tem dona — ela só entra depois da Season 1.
 */
export const REGIAO_DONA: Record<string, number> = {
  esfolagem: 0, // Planalto Solar
  couraria: 0,
  herbalismo: 1, // Floresta Esmeralda
  alquimia: 1,
  mineracao: 2, // Terras Devastadas
  forja: 2,
};

/**
 * A coleta que dá vocação à região — a que ela é dona.
 *
 * <p>Deriva de {@link REGIAO_DONA} em vez de ter tabela própria: as duas diziam a mesma
 * coisa, e duas tabelas dizendo a mesma coisa divergem no primeiro acerto.
 */
export const vocacaoDa = (regiaoSlot: number): Pericia | undefined =>
  COLETAS.find((p) => REGIAO_DONA[p.id] === regiaoSlot);

/** Os tiers que esta perícia aceita nesta região. Vazio para a que ainda não entrou. */
export function tiersPossiveis(pericia: Pericia, regiaoSlot: number): number[] {
  if (pericia.depoisDaSeason1) return [];
  return REGIAO_DONA[pericia.id] === regiaoSlot ? [...TIERS_ESCOLHIDOS] : [3];
}

/** O teto de cada perícia escolhida, por área: `{ "regiaoSlot-slot": { forja: 4 } }`. */
export type TetosPorArea = Record<string, Record<string, number>>;

/** A região de uma chave `regiaoSlot-slot`. */
const regiaoDaChave = (chave: string) => Number(chave.split('-')[0]);

/**
 * Lê o que veio gravado e descarta o que a regra não aceita.
 *
 * <p>Descartar, e não corrigir: um T5 de Forja gravado no Planalto não tem tradução
 * honesta — rebaixá-lo para T3 seria inventar uma escolha que ninguém fez. E é por aqui
 * que a regra se aplica também ao que foi escrito antes dela mudar.
 */
export function interpretarTetos(guardado: unknown): TetosPorArea {
  if (!guardado || typeof guardado !== 'object') return {};
  const tetos: TetosPorArea = {};
  for (const [chave, porPericia] of Object.entries(guardado as Record<string, unknown>)) {
    if (!porPericia || typeof porPericia !== 'object') continue;
    const regiao = regiaoDaChave(chave);
    const validos: Record<string, number> = {};
    for (const [id, tier] of Object.entries(porPericia as Record<string, unknown>)) {
      const pericia = PERICIAS.find((p) => p.id === id);
      if (pericia && typeof tier === 'number' && tiersPossiveis(pericia, regiao).includes(tier)) {
        validos[id] = tier;
      }
    }
    if (Object.keys(validos).length) tetos[chave] = validos;
  }
  return tetos;
}

/**
 * Troca o teto de uma perícia numa área. `null` volta ao T2 que toda área já tem.
 *
 * <p>A área que fica sem nenhuma escolha sai do documento, para o "nada marcado" ter uma
 * forma só.
 */
export function definirTeto(
  tetos: TetosPorArea,
  chave: string,
  pericia: string,
  tier: number | null,
): TetosPorArea {
  const daArea = { ...(tetos[chave] ?? {}) };
  if (tier == null) delete daArea[pericia];
  else daArea[pericia] = tier;

  const resto = { ...tetos };
  if (Object.keys(daArea).length) resto[chave] = daArea;
  else delete resto[chave];
  return resto;
}

/**
 * As perícias que a região ainda não tem em T3 ou acima.
 *
 * <p>É o que a regra cobra — toda região tem T3 de todas — e o que a tela aponta,
 * em vez de deixar o time descobrir contando checkbox. Um T4 ou T5 conta: o teto inclui
 * os tiers de baixo.
 */
export function semT3NaRegiao(tetos: TetosPorArea, regiaoSlot: number): Pericia[] {
  const daRegiao = Object.entries(tetos).filter(([chave]) => regiaoDaChave(chave) === regiaoSlot);
  return PERICIAS.filter(
    (p) => !p.depoisDaSeason1 && !daRegiao.some(([, porPericia]) => (porPericia[p.id] ?? 0) >= 3),
  );
}
